import { Link } from "react-router-dom";
import { ArrowUpRight, EnvelopeSimple, MapPin, ShieldCheck } from "@phosphor-icons/react";
import { motion } from "framer-motion";
import Seo from "@/components/Seo";

const SECTIONS = [
  {
    id: "who-we-are",
    num: "01",
    title: "Who are we?",
    body: [
      "Organizing an event is always an important challenge for agencies. They spend all their energy in thousands of details in order to have happy visitors and organizers.",
      "OneTec's goal is to improve organizers' efficiency and to create a unique customer experience by using cutting-edge innovation.",
      "OneTec's story starts as a Software Developers' Company in early 2000. The first major event OneTec worked for was in 2002 on a huge international medical congress and since, OneTec never stopped working in this sector.",
      "The expansion of OneTec occurred by organic growth, but also with some major acquisitions: Cerix (Belgian leading company in short-term IT rental), AIM (Voting Systems), Whos-in (ticketing and access control), Getyoo (interactive services for visitors on public and professional fairs), Bebotics (photobooths and custom event experiences) and Eventattitude (Brand Activation interactive & personalised experiences).",
      "Today, OneTec's services consist of 3 main axes: Event Technology, Brand Activation and IT/AV rental.",
      "We take your privacy seriously and are committed to comply with data protection laws, and especially the General Data Protection Regulation (GDPR).",
      "OneTec is a company existing and operating under Belgian law, with statutory seat located at Tollaan 75, 1932 Zaventem and registered at the Crossroads Bank for Enterprises under company number 0478.889.493.",
      "We use our best efforts to bring the data processing activities of OneTec into compliance with applicable data protection legislation, including Regulation (EU) 2016/679 (GDPR) and the Belgian Data Protection Act of 8 December 1992, each as applicable and amended from time to time.",
    ],
  },
  {
    id: "which-data",
    num: "02",
    title: "Which data?",
    body: [
      "Considered as confidential information within the meaning of this Regulation: all employer information that has not been made public. Confidential information that has been made public illegally must also be considered as confidential information.",
      "All personal data, as defined by the GDPR, namely:",
    ],
    quote:
      "« Any information relating to an identified or identifiable natural person; an 'identifiable individual' meaning a natural person who can be identified, directly or indirectly, in particular by reference to an identifier such as a name, an identification number, location data, online id, or to one or more factors specific to their physical, physiological, genetic, mental, economic, cultural or social identity. »",
    afterQuote: ["Hereinafter referred to as the \u201cconfidential information\u201d."],
    list: {
      title: "We most commonly — but not always — process the following categories of confidential information about you:",
      items: [
        "Your personal data (last name, first name, address, city, country)",
        "Your professional data (company, job title)",
        "Your email address",
        "The picture representing you and the other persons on the picture",
        "On an aggregated basis, limited socio-demographic data: gender (female/male/undefined), age (or range), language",
        "The answers that were asked by our client and that you were invited to fill in",
      ],
    },
    list2: {
      title: "We process your confidential information for the following purposes, only with your consent:",
      items: [
        "Sending your registration confirmation, reminder, thank-you message or post-event survey",
        "Sending documents related to the event",
        "Sending your picture to your email address",
        "Posting your picture on a privately accessible photo gallery",
        "Transferring your picture and answers to our client so they can know you better. Our client is not authorised to use your personal data for marketing purposes and will not use your picture for corporate communications unless consent was asked and given by you",
        "Transferring your picture and email address to our client so you can receive special offers and promotions",
        "Transferring your picture to our client so it may be used for corporate internal or external communications (e.g. social media or website)",
        "Transferring your picture and email address to our client and providing socio-demographic insights to help tailor marketing activities",
      ],
    },
  },
  {
    id: "employees",
    num: "03",
    title: "Employees' commitment",
    body: [
      "This regulation contains directives to be respected by all OneTec's employees in the exercise of their functions. Employees come into contact with confidential information in the performance of their duties. Employees must maintain confidential information at any time under the seal of secrecy and won't share it with anyone other than authorised colleagues, after checking, to access the confidential information in question.",
      "The employees commit not to share confidential information voluntarily or involuntarily with unauthorised persons, including third parties.",
      "The employees also commit to never use the confidential information at the expense of the employer, or for any other purpose than those of their mission.",
      "These commitments are the subject of an internal contract agreed by all employees, including members of the Board of Directors, the management, volunteers, interns, sub-processors, clients, etc. (hereinafter the \u201ccollaborators\u201d) and, therefore, any person who comes into contact with confidential employer information or information that may be considered as personal data under the GDPR regulation.",
    ],
  },
  {
    id: "your-rights",
    num: "04",
    title: "Your rights",
    body: [
      "Subject to applicable data protection laws, you have the rights to access, rectify and erase your personal data, the rights to object to or limit the processing of your personal data and the right to data portability, meaning that:",
    ],
    list: {
      items: [
        "You have the right to be informed about how we use your personal data and how to exercise your rights.",
        "You have the right to be provided with clear, transparent and easily understandable information about how we use your personal data and your rights — this is mentioned on every screen we use to collect your personal information, and you have the right not to complete it.",
        "You have the right to obtain access to your personal data, so you are aware and can check that we are using your personal data in accordance with applicable data protection laws.",
        "You have the right to request the deletion of your personal data where there is no compelling reason for us to keep using it. Note that this is not an absolute right to erasure and exceptions apply.",
        "You are entitled to have your personal data rectified if it is inaccurate or incomplete.",
        "You have the 'right to be forgotten' which enables you to request the deletion or removal of your personal data where there is no compelling reason for us to keep using it.",
        "You have the right to 'block' or suppress further use of your personal data. When processing is restricted, we can still store your personal data, but may not use it further.",
        "You have the right to obtain and reuse your personal data for your own purposes across different services.",
        "You have the right to lodge a complaint about the way we handle or process your personal data with your national data protection authority.",
        "If you have given your consent, you have the right to withdraw your consent at any time (though doing so does not mean anything we have done with your personal data with your consent up to that point is unlawful).",
      ],
    },
    afterList: [
      "Please forward any request regarding your rights as a data subject to us by email at info@onetec.eu. We will comply with your request as soon as reasonably practicable and always within the timeframes set forth by applicable data protection law. Please note that we may need to retain certain of your personal data for certain purposes as required or authorised by law. We may also ask you for proof of identity if we have a doubt.",
    ],
  },
  {
    id: "security",
    num: "05",
    title: "Protection of confidential information / Security measures",
    body: [
      "Legally, if we received your personal data with your consent and with a legitimate interest to collect and disclose it to our client, we are within our rights.",
      "We will ask for your free, prior and informed consent if our clients want to use your personal data for direct marketing purposes or want to publish your picture online (on their social media or websites) or use it for internal or external corporate communications. By giving your consent to our client, you irrevocably waive any claim for (economic) compensation for the use by our client of your picture or personal data.",
      "The provision of your personal data is necessary for the legitimate interests pursued by OneTec provided that these interests prevail over your fundamental rights and freedoms.",
      "We do not subject you to decisions based exclusively on automated processing that produce legal effects concerning you or similarly significantly affect you. The provision of your picture and personal data (i.e. email address) is a necessary condition for the provision of our service. If we cannot process your picture or your email address, you may not use our service.",
      "We do not collect personal data about you from third parties.",
      "We have analysed the risks and reassess them specifically for each data collected (implementation of measures adapted to the risk). Treatment in the legitimate interests of the customer is governed by a contract which regulates the use of the data and the protection. Data collection is done according to the rules of the GDPR: always with application for free, revealed, specific consent by an active event.",
      "In addition, you can read all security measures taken by us, mentioned in this Privacy Policy.",
    ],
  },
  {
    id: "transport",
    num: "06",
    title: "Transport of confidential information",
    body: [
      "In case of transport of confidential information (e.g. on a USB key or a laptop outside of the business premises), employees take into account the following guidelines:",
    ],
    list: {
      items: [
        "Employees limit the transport of confidential information within and exclusively to what is strictly necessary for the performance of their work.",
        "Employees need to be permanently aware of the risk of data loss in this type of situation.",
        "Employees must do everything in their power to prevent the theft or loss of data (e.g. forgetting their laptop/smartphone on the train, theft of their laptop left in the car…).",
        "Employees must never leave their laptop unattended outside of the workplace. Employee victims of a theft of their laptop, smartphone or tablet inform their hierarchical superior without delay and at the latest within six (6) hours of discovery. Employees who find that their laptop, smartphone or tablet has been hacked must inform their superior within six (6) hours of the finding.",
        "Employees who use another wireless network than that of the employer must verify if this is a secure network. It is also forbidden for employees to use an open and unsecured WIFI network.",
      ],
    },
    afterList: [
      "These commitments are the subject of a contract agreed by all employees, including members of the Board of Directors, management, volunteers, interns, sub-processors, clients, etc. (the \u201ccollaborators\u201d) and, therefore, any person who comes into contact with confidential employer information or information that may be considered personal data under the GDPR.",
    ],
  },
  {
    id: "storage",
    num: "07",
    title: "Where is my confidential information stored?",
    body: [
      "All data are stored in a SQL database, secured by an encrypted login and password. The data center is located in Europe and duplicated in Belgium for security reasons.",
      "The data will be provided to our client, if allowed by GDPR, through an encrypted file.",
      "The data at rest will be deleted by OneTec at the end of the mission.",
      "Outside OneTec: the servers are physically located at Net7 — our GDPR-compliant subcontractor located in Belgium — and OVH. Inside OneTec: the data only carry through the officers in charge of the project. Each computer is protected by a personal password. The OneTec building is protected by an alarm system.",
    ],
  },
  {
    id: "retention",
    num: "08",
    title: "How long do we retain your personal data?",
    body: [
      "We retain your personal data as long as necessary to achieve the purposes for which we process your personal data. OneTec and our clients and sub-processors do not keep data longer than what is necessary to carry out the mission for which it was collected and won't pass it on to any third party without explicit authorisation by the participating customers.",
    ],
    list: {
      title: "We use the following criteria to determine the retention periods of personal data:",
      items: [
        "The time elapsed since the event",
        "The sensitivity of the personal data",
        "Security reasons (for example, the security of our information security systems)",
        "Any current or potential dispute or litigation",
        "Any legal or regulatory obligation to retain or delete personal data",
      ],
    },
    afterList: [
      "When you are invited to give your consent, you are always informed about the retention period so you can agree to the use of your data knowingly.",
    ],
  },
  {
    id: "marketing",
    num: "09",
    title: "How can I stop receiving marketing e-mails?",
    body: [
      "If you would like to stop receiving e-mails from our clients, you can opt out at any time by unsubscribing from the mailing list of our client or by sending them an email explaining you do not want to receive their newsletter anymore.",
      "Our clients must provide you with the possibility to object to receiving any type of e-mails — e.g. through a link to unsubscribe from their mailing list or by allowing you to send an email to them.",
      "You have the right to object at any time to the processing of your personal data for direct marketing purposes.",
      "You always have the right to lodge a complaint at the competent data protection authority.",
    ],
    authority: {
      title:
        "Belgian Data Protection Authority / Autorité de protection des données / Gegevensbeschermingsautoriteit",
      lines: [
        "Rue de la Presse, 35, 1000 Bruxelles / Drukpersstraat 35, 1000 Brussel",
        "commission@privacycommission.be",
        "+32 (0)2 274 48 00",
      ],
    },
  },
  {
    id: "contact",
    num: "10",
    title: "I have a question or a problem. Who can I contact?",
    body: [
      "If you have any question or complaint about the processing of your personal data, please contact us by email at info@onetec.eu or by post at Tollaan 75, 1932 Zaventem (Belgium).",
    ],
  },
];

const ACCENTS = [
  "bg-ot-peach",
  "bg-ot-mint",
  "bg-ot-coral",
  "bg-ot-peach",
  "bg-ot-mint",
  "bg-ot-coral",
  "bg-ot-peach",
  "bg-ot-mint",
  "bg-ot-coral",
  "bg-ot-peach",
];

export default function PrivacyPolicy() {
  return (
    <div data-testid="privacy-page">
      <Seo
        title="Privacy Policy"
        description="OneTec SRL Privacy Policy — how we collect, process, store and protect your personal data under the GDPR."
        path="/privacy-policy"
        type="article"
        schema={{
          "@context": "https://schema.org",
          "@type": "WebPage",
          name: "Privacy Policy — OneTec",
          description:
            "OneTec SRL Privacy Policy — GDPR-compliant data processing rules.",
          inLanguage: "en",
        }}
      />

      {/* Hero */}
      <section className="border-b-2 border-ot-fg">
        <div className="max-w-6xl mx-auto px-5 md:px-10 pt-20 md:pt-28 pb-12 md:pb-16 relative">
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="inline-flex items-center gap-2 text-xs uppercase tracking-widest font-bold text-ot-indigo bg-white border-2 border-ot-fg px-3 py-1.5"
          >
            <ShieldCheck size={14} weight="bold" /> Legal · GDPR
          </motion.div>
          <motion.h1
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.05 }}
            className="font-display text-5xl sm:text-6xl lg:text-7xl uppercase tracking-tighter leading-[0.92] mt-5"
          >
            Privacy <span className="bg-ot-peach border-2 border-ot-fg px-2">Policy</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="mt-6 text-base md:text-lg text-ot-fg/70 max-w-2xl"
          >
            This is the &ldquo;regulation on the Protection of privacy&rdquo; (hereinafter the
            &ldquo;rules&rdquo;) of OneTec SRL (hereinafter &ldquo;OneTec&rdquo; or the &ldquo;employer&rdquo;).
            We take your privacy seriously and are committed to comply with data protection laws,
            especially the General Data Protection Regulation (GDPR).
          </motion.p>
        </div>
      </section>

      {/* Body */}
      <section className="px-5 md:px-10 py-12 md:py-20">
        <div className="max-w-6xl mx-auto grid lg:grid-cols-12 gap-10">
          {/* Sticky TOC */}
          <aside className="lg:col-span-4">
            <div className="lg:sticky lg:top-24">
              <p className="text-xs uppercase tracking-widest font-bold text-ot-indigo mb-3">
                Contents
              </p>
              <nav
                className="nb-card p-4 space-y-1"
                aria-label="Privacy policy sections"
                data-testid="privacy-toc"
              >
                {SECTIONS.map((s, i) => (
                  <a
                    key={s.id}
                    href={`#${s.id}`}
                    className="block px-3 py-2 text-sm hover:bg-ot-peach border-2 border-transparent hover:border-ot-fg font-display uppercase tracking-tight transition-colors"
                  >
                    <span className="text-ot-indigo font-bold mr-2">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    {s.title}
                  </a>
                ))}
              </nav>
              <div className="mt-6 nb-card p-5 bg-ot-mint">
                <p className="text-xs uppercase tracking-widest font-bold mb-2">
                  Data request
                </p>
                <p className="text-sm">
                  Exercise your rights or ask a question about your personal data.
                </p>
                <a
                  href="mailto:info@onetec.eu"
                  className="nb-btn mt-4 text-sm w-full justify-center"
                  data-testid="privacy-contact-dpo"
                >
                  <EnvelopeSimple weight="bold" /> info@onetec.eu
                </a>
              </div>
            </div>
          </aside>

          {/* Sections */}
          <div className="lg:col-span-8 space-y-14">
            {SECTIONS.map((s, i) => (
              <motion.article
                key={s.id}
                id={s.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.45 }}
                className="scroll-mt-28"
                data-testid={`privacy-section-${s.id}`}
              >
                <div className="flex items-start gap-4 mb-5">
                  <span
                    className={`${ACCENTS[i % ACCENTS.length]} border-2 border-ot-fg shadow-[4px_4px_0px_var(--ot-fg)] font-display text-xl uppercase tracking-tight px-3 py-1.5 leading-none`}
                  >
                    {s.num}
                  </span>
                  <h2 className="font-display text-2xl sm:text-3xl uppercase tracking-tighter leading-tight">
                    {s.title}
                  </h2>
                </div>

                <div className="prose-content space-y-4 text-ot-fg/85 leading-relaxed">
                  {s.body?.map((p, idx) => <p key={`b-${idx}`}>{p}</p>)}

                  {s.quote && (
                    <blockquote className="border-l-4 border-ot-indigo bg-white border-2 border-ot-fg p-5 italic">
                      {s.quote}
                    </blockquote>
                  )}
                  {s.afterQuote?.map((p, idx) => <p key={`aq-${idx}`}>{p}</p>)}

                  {s.list && (
                    <div>
                      {s.list.title && (
                        <p className="font-semibold mt-2">{s.list.title}</p>
                      )}
                      <ul className="mt-3 space-y-2">
                        {s.list.items.map((it, idx) => (
                          <li
                            key={`l-${idx}`}
                            className="flex gap-3 pl-1"
                          >
                            <span
                              aria-hidden
                              className="mt-2 w-2 h-2 bg-ot-indigo shrink-0"
                            />
                            <span>{it}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {s.list2 && (
                    <div className="mt-2">
                      {s.list2.title && (
                        <p className="font-semibold mt-2">{s.list2.title}</p>
                      )}
                      <ul className="mt-3 space-y-2">
                        {s.list2.items.map((it, idx) => (
                          <li
                            key={`l2-${idx}`}
                            className="flex gap-3 pl-1"
                          >
                            <span
                              aria-hidden
                              className="mt-2 w-2 h-2 bg-ot-coral shrink-0"
                            />
                            <span>{it}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {s.afterList?.map((p, idx) => <p key={`al-${idx}`}>{p}</p>)}

                  {s.authority && (
                    <div className="nb-card p-5 bg-ot-bg">
                      <p className="font-display uppercase text-sm tracking-tight">
                        {s.authority.title}
                      </p>
                      <div className="mt-3 space-y-1 text-sm text-ot-fg/80">
                        {s.authority.lines.map((line, idx) => (
                          <p key={idx}>{line}</p>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </motion.article>
            ))}

            {/* Closing card */}
            <div
              className="nb-card p-6 md:p-8 bg-ot-indigo text-white border-2 border-ot-fg"
              data-testid="privacy-closing-card"
            >
              <p className="text-xs uppercase tracking-widest text-ot-peach font-bold">
                Get in touch
              </p>
              <h3 className="font-display text-2xl md:text-3xl uppercase tracking-tighter mt-2">
                Questions about your data?
              </h3>
              <p className="mt-3 text-white/80">
                Email us at{" "}
                <a
                  href="mailto:info@onetec.eu"
                  className="underline hover:text-ot-peach"
                >
                  info@onetec.eu
                </a>{" "}
                or write to:
              </p>
              <p className="mt-3 flex items-start gap-3 text-white/90">
                <MapPin size={20} weight="bold" className="mt-1 shrink-0" />
                <span>
                  OneTec SRL
                  <br />
                  Tollaan 75
                  <br />
                  1932 Zaventem, Belgium
                </span>
              </p>
              <div className="mt-6 flex flex-wrap gap-3">
                <Link
                  to="/contact"
                  className="nb-btn bg-ot-peach text-ot-fg border-ot-fg text-sm"
                >
                  Contact us <ArrowUpRight weight="bold" />
                </Link>
                <Link to="/" className="nb-btn nb-btn-ghost text-sm !text-white !border-white hover:!bg-white hover:!text-ot-fg">
                  Back to home
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
